import { z } from 'zod';

/** `{id, name}` option for the corporate-booking pickers. Kept local —
 * mirrors voll-business `BusinessOption` structurally. */
export const ChatUiOptionSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  /** Código externo do cadastro (só centros de custo o trazem). O label do item é
   *  `code - name` quando presente — clientes que deixam o `name` vazio dependem
   *  dele para a opção não renderizar em branco. */
  code: z.string().optional(),
});
export type ChatUiOption = z.infer<typeof ChatUiOptionSchema>;

/** A selectable company in the booking form. Cost centers AND projects are
 * company-scoped, so each company carries its own lists — the agent pre-fetches
 * projects per company so switching company on the (static) form re-filters both
 * pickers without a server round-trip. */
export const ChatUiBookingCompanySchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  /** `true` ⇒ a empresa libera TODOS os centros de custo e `costCenters` NÃO é
   *  exaustivo (tipicamente vazio, por ser grande demais para embutir). Lista
   *  vazia então significa "busque no servidor", não "não há opção". Opcional:
   *  forms antigos/fixtures sem o campo seguem com o catálogo embutido. */
  allCostCenters: z.boolean().optional(),
  costCenters: z.array(ChatUiOptionSchema),
  projects: z.array(ChatUiOptionSchema),
});

/** Aparato corporativo retornado por GET /{rental,flight}/booking-form — o BFF
 *  monta, o web-chat parseia na borda. Mesma família de BookingSelectionsSchema. */
export const CorporateBookingFormSchema = z.object({
  companies: z.array(ChatUiBookingCompanySchema),
  // Default company/cost-center são OPCIONAIS: um viajante (tipicamente um colega)
  // pode não ter um centro de custo default — o form ainda é montado e o requester
  // escolhe a alocação no drawer (seedAllocation cai em vazio; canSubmit exige a
  // escolha). Ausentes também quando `companies` está vazio (sem acesso corporativo).
  defaultCompanyId: z.string().optional(),
  defaultCostCenterId: z.string().optional(),
  motives: z.array(ChatUiOptionSchema),
  justifications: z.array(ChatUiOptionSchema),
  // Client-level config (businessCurrentClient.enableProjectField) — gates the
  // project picker in the new product_checkout allocation field. Optional/default
  // false: older forms (tests, cached fixtures) without the field stay disabled.
  enableProjectField: z.boolean().optional(),
  // A flag `allow_many_allocations` de quem reserva (paridade com o legado): libera
  // o rateio em até `MAX_COST_CENTER_SPLIT_ROWS` linhas por viajante (#6697).
  // Ausente ⇒ uma linha só, como antes.
  allowManyAllocations: z.boolean().optional(),
  // Detalhe do viajante. `name`/`document` são o resumo display-only compartilhado;
  // os demais campos alimentam o card de Viajante editável (todos opcionais).
  // Shared with rental: `document.type` includes 'dni'; `missingRequired` includes all
  // four blocking fields (nationality + gender are rental-blocking). Flight UI narrows
  // rendering in the drawer, NOT in this shared schema.
  traveler: z
    .object({
      // Authoritative business-user identity resolved for this form. Optional for
      // legacy/single-product payload compatibility; multi-traveler checkout
      // requires an exact match with the requested TravelerRef before rendering.
      id: z.string().min(1).optional(),
      name: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      birthdate: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      nationality: z
        .object({ code: z.string().optional(), name: z.string().optional() })
        .optional(),
      gender: z.enum(['F', 'M']).optional(),
      document: z
        .object({
          type: z.enum(['cpf', 'passport', 'dni']),
          value: z.string(),
          // País emissor (ISO 3166-1 alpha-2) e validade do documento de viagem —
          // editáveis no card de Viajante da locação, vão para `documents[]` upstream.
          issuingCountry: z.string().optional(),
          expirationDate: z.string().optional(),
        })
        .optional(),
      // Dados mascarados em sistema. CPF é read-only; passaporte/DNI alimentam
      // placeholders dos campos editáveis e podem ser reenviados para sinalizar
      // reuso do documento on-file.
      maskedInfo: z
        .object({
          cpf: z.string().optional(),
          passport: z.string().optional(),
          passportCountry: z.string().optional(),
          passportValidate: z.string().optional(),
          dni: z.string().optional(),
        })
        .optional(),
      // `embark` (boarding name + surname) is flight-only — additive member alongside
      // the shared four (birthdate/nationality/gender/document).
      missingRequired: z
        .array(z.enum(['birthdate', 'nationality', 'gender', 'document', 'embark']))
        .optional(),
    })
    .optional(),
  // Country list for the passport país-emissor picker. Optional — populated by GET
  // /flight/booking-form (t6); absent on rental forms or when the places op is unavailable.
  countries: z.array(z.object({ code: z.string(), name: z.string() })).optional(),
});
export type CorporateBookingForm = z.infer<typeof CorporateBookingFormSchema>;
